import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { useStore } from '../../../store';
import { hasPermission } from '../../utils/permissions';
import { useToast } from '../../hooks/useToast';
import { fetchLicense, uploadLicense, type LicenseInfo } from '../../services/LicenseService';

/**
 * Administration → Licence: shows the licence of the current planning area and lets an
 * administrator upload a new key from the licence tool (licence decision E7).
 */
const LicensePage = () => {
  const { t, i18n } = useTranslation(['common']);
  const { showToast } = useToast();
  const userProfile = useStore((s) => s.userProfile);
  const reloadProfile = useStore((s) => s.reloadProfile);
  const canEdit = hasPermission('system:settings:edit', userProfile);

  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [keyText, setKeyText] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLicense(await fetchLicense());
    } catch (err) {
      console.error('Failed to load licence', err);
      setLicense(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return null;
    const [year, month, day] = iso.split('-').map(Number);
    return new Intl.DateTimeFormat(i18n.language === 'de' ? 'de-DE' : 'en-US', { dateStyle: 'medium' })
      .format(new Date(year, month - 1, day));
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setKeyText((await file.text()).trim());
    setUploadError(null);
    event.target.value = '';
  };

  const handleUpload = async () => {
    if (!keyText.trim()) return;
    setSaving(true);
    setUploadError(null);
    try {
      const updated = await uploadLicense(keyText.trim());
      setLicense(updated);
      setKeyText('');
      showToast('success', t('license.page.uploadSuccessTitle'), t('license.page.uploadSuccess'));
      // Menu and perspectives follow the new modules without a new login.
      await reloadProfile().catch((err) => console.warn('Profile reload after licence upload failed', err));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      const reason = error.response?.data?.message || error.message || '';
      setUploadError(t('license.page.uploadInvalid', { reason }));
    } finally {
      setSaving(false);
    }
  };

  const row = (label: string, value: React.ReactNode) => (
    <div className="flex py-2 border-b last:border-b-0">
      <div className="w-56 shrink-0 text-gray-600">{label}</div>
      <div className="flex-1">{value}</div>
    </div>
  );

  return (
    <div className="p-6 max-w-3xl" data-testid="license-page">
      <h1 className="text-2xl font-semibold mb-4">{t('routes.license')}</h1>

      {loading && <div className="text-gray-500">{t('license.page.loading')}</div>}

      {!loading && license && (
        <>
          {license.expired && (
            <div className="mb-4 p-3 rounded bg-red-100 text-red-800" role="alert">
              {t('license.page.expired', { date: formatDate(license.validUntil) })}
            </div>
          )}
          {!license.expired && license.expiryNotice && (
            <div className="mb-4 p-3 rounded bg-yellow-100 text-yellow-900" role="status">
              {t('license.notice.expiring', { count: license.daysUntilExpiry ?? 0, date: formatDate(license.validUntil) })}
            </div>
          )}

          <div className="bg-white rounded shadow-sm border px-4 mb-6">
            {row(t('license.page.planningArea'), license.tenantName)}
            {row(t('license.page.source'), t(`license.source.${license.source}`))}
            {license.customer && row(t('license.page.customer'), license.customer)}
            {row(t('license.page.modules'), (
              <div className="flex flex-wrap gap-2">
                {license.modules.length === 0 && <span className="text-gray-500">{t('license.page.noModules')}</span>}
                {license.modules.map((m) => (
                  <span key={m} className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-sm">
                    {t(`license.modules.${m}`)}
                  </span>
                ))}
              </div>
            ))}
            {license.issuedAt && row(t('license.page.issuedAt'), formatDate(license.issuedAt))}
            {row(t('license.page.validUntil'), formatDate(license.validUntil) ?? t('license.page.unlimited'))}
            {license.maintenanceUntil && row(t('license.page.maintenanceUntil'), formatDate(license.maintenanceUntil))}
          </div>
        </>
      )}

      {!loading && !license && (
        <div className="mb-6 text-red-700">{t('license.page.loadFailed')}</div>
      )}

      {canEdit && (
        <div className="bg-white rounded shadow-sm border p-4">
          <h2 className="text-lg font-semibold mb-2">{t('license.page.uploadTitle')}</h2>
          <p className="text-sm text-gray-600 mb-3">{t('license.page.uploadHint')}</p>
          <textarea
            className="e-input w-full font-mono text-xs h-32 p-2 border rounded"
            value={keyText}
            onChange={(e) => { setKeyText(e.target.value); setUploadError(null); }}
            placeholder={t('license.page.keyPlaceholder')}
            data-testid="license-key-input"
          />
          {uploadError && <div className="text-red-700 text-sm mt-2" role="alert">{uploadError}</div>}
          <div className="flex gap-2 mt-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".lic,.jws,.txt,text/plain"
              className="hidden"
              onChange={handleFile}
            />
            <ButtonComponent cssClass="e-outline" onClick={() => fileInputRef.current?.click()}>
              {t('license.page.chooseFile')}
            </ButtonComponent>
            <ButtonComponent
              cssClass="e-primary"
              disabled={saving || !keyText.trim()}
              onClick={handleUpload}
              data-testid="license-upload-button"
            >
              {t('license.page.upload')}
            </ButtonComponent>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicensePage;
